import { useState, useCallback, useEffect, useRef } from "react";
import usePageTitle from "../../hooks/usePageTitle";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";

const stakeholders = [
  { id: 1, name: "New Customers", href: "#", initial: "NC", current: true },
  { id: 2, name: "Returning Buyers", href: "#", initial: "RB", current: false },
  { id: 3, name: "Enterprise Leads", href: "#", initial: "EL", current: false },
  { id: 4, name: "Churned Users", href: "#", initial: "CU", current: false },
  { id: 5, name: "Free Trial Users", href: "#", initial: "FT", current: false },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function DashboardNewPage() {
  usePageTitle("Dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [panelWidth, setPanelWidth] = useState(384);
  const isDragging = useRef(false);
  const rafRef = useRef(null);
  const containerRef = useRef(null);

  const MIN_PANEL = 200;
  const MAX_PANEL = 700;

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  useEffect(() => {
    const suppressResizeObserverError = (e) => {
      if (
        e.message === "ResizeObserver loop completed with undelivered notifications." ||
        e.message === "ResizeObserver loop limit exceeded"
      ) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", suppressResizeObserverError);
    return () => window.removeEventListener("error", suppressResizeObserverError);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const newWidth = window.innerWidth - e.clientX;
        setPanelWidth(Math.min(MAX_PANEL, Math.max(MIN_PANEL, newWidth)));
      });
    };
    const handleMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <>
      {/*
        This example requires updating your template:

        ```
        <html class="h-full bg-white">
        <body class="h-full">
        ```
      */}
      <div>
        <Dialog
          open={sidebarOpen}
          onClose={setSidebarOpen}
          className="relative z-50 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="size-6 text-white"
                    />
                  </button>
                </div>
              </TransitionChild>

              <div className="relative flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
                <div className="relative flex h-16 shrink-0 items-center">
                  <img
                    alt="Your Company"
                    src="https://tailwindcss.com/plus-assets/img/logos/mark.svg?color=indigo&shade=600"
                    className="h-8 w-auto"
                  />
                </div>
                <nav className="relative flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <div className="text-xs/6 font-semibold text-gray-400">
                        Stakeholders
                      </div>
                      <ul role="list" className="-mx-2 mt-2 space-y-1">
                        {stakeholders.map((item) => (
                          <li key={item.id}>
                            <a
                              href={item.href}
                              className={classNames(
                                item.current
                                  ? "bg-gray-50 text-indigo-600"
                                  : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600",
                                "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                              )}
                            >
                              <span
                                className={classNames(
                                  item.current
                                    ? "border-indigo-600 text-indigo-600"
                                    : "border-gray-200 text-gray-400 group-hover:border-indigo-600 group-hover:text-indigo-600",
                                  "flex size-6 shrink-0 items-center justify-center rounded-lg border bg-white text-[0.625rem] font-medium",
                                )}
                              >
                                {item.initial}
                              </span>
                              <span className="truncate">{item.name}</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          <div className="relative flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6">
            <div className="relative flex h-16 shrink-0 items-center">
              <img
                alt="Your Company"
                src="https://tailwindcss.com/plus-assets/img/logos/mark.svg?color=indigo&shade=600"
                className="h-8 w-auto"
              />
            </div>
            <nav className="relative flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <div className="text-xs/6 font-semibold text-gray-400">
                    Stakeholders
                  </div>
                  <ul role="list" className="-mx-2 mt-2 space-y-1">
                    {stakeholders.map((item) => (
                      <li key={item.id}>
                        <a
                          href={item.href}
                          className={classNames(
                            item.current
                              ? "bg-gray-50 text-indigo-600"
                              : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600",
                            "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                          )}
                        >
                          <span
                            className={classNames(
                              item.current
                                ? "border-indigo-600 text-indigo-600"
                                : "border-gray-200 text-gray-400 group-hover:border-indigo-600 group-hover:text-indigo-600",
                              "flex size-6 shrink-0 items-center justify-center rounded-lg border bg-white text-[0.625rem] font-medium",
                            )}
                          >
                            {item.initial}
                          </span>
                          <span className="truncate">{item.name}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
                <li className="-mx-6 mt-auto">
                  <a
                    href="#"
                    className="flex items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    <img
                      alt=""
                      src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                      className="size-8 rounded-full bg-gray-50 outline -outline-offset-1 outline-black/5"
                    />
                    <span className="sr-only">Your profile</span>
                    <span aria-hidden="true">Tom Cook</span>
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-xs sm:px-6 lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="relative -m-2.5 p-2.5 text-gray-700 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
          <div className="relative flex-1 text-sm/6 font-semibold text-gray-900">
            Dashboard
          </div>
          <a href="#" className="relative">
            <span className="sr-only">Your profile</span>
            <img
              alt=""
              src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
              className="size-8 rounded-full bg-gray-50 outline -outline-offset-1 outline-black/5"
            />
          </a>
        </div>

        <div
          ref={containerRef}
          className="lg:pl-72"
          style={{
            display: "flex",
            flexDirection: "row",
            height: "100vh",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* Goals section */}
          <main style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
            <div className="px-4 py-6 sm:px-6 lg:px-8">
              <h2 className="text-lg font-semibold text-gray-900">Goals</h2>
            </div>
            <div style={{ height: "1px", backgroundColor: "#e5e7eb" }} />
            <div className="px-4 py-4 sm:px-6 lg:px-8">
              {/* Goals content */}
            </div>
          </main>

          {/* Draggable divider — only when workflow is open */}
          {showWorkflow && (
            <div
              onMouseDown={handleMouseDown}
              style={{
                width: "6px",
                cursor: "col-resize",
                backgroundColor: "transparent",
                flexShrink: 0,
                position: "relative",
                zIndex: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "1px",
                  height: "100%",
                  backgroundColor: "#e5e7eb",
                }}
              />
            </div>
          )}

          {/* Workflow panel — only when open */}
          {showWorkflow && (
            <div
              style={{
                width: `${panelWidth}px`,
                flexShrink: 0,
                overflowY: "auto",
              }}
            >
              <div
                className="px-4 py-6 sm:px-6 lg:px-8"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h2 className="text-lg font-semibold text-gray-900">
                  Workflow
                </h2>
                <button
                  onClick={() => setShowWorkflow(false)}
                  className="text-gray-400 hover:text-gray-600"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "1.25rem",
                    lineHeight: 1,
                    padding: "0.25rem",
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ height: "1px", backgroundColor: "#e5e7eb" }} />
              <div className="px-4 py-4 sm:px-6 lg:px-8">
                {/* Workflow content */}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
